import type { SyncToExternalsService } from '.';
import { effect } from '@preact/signals-react';
import type { AnalyticsService } from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { DatadogService } from '@thinkalpha/ui-ioc/core/types/DatadogService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { PERSISTED_CURRENT_USER } from 'src/features/local-storage';
import { localforage } from 'src/lib/serialization/localForage';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';

/**
 * Syncs the effective current user to external services.
 */
@injectable()
export class SyncToExternalsServiceImpl implements SyncToExternalsService {
    readonly #disposableStack = new DisposableStack();
    #logger: Logger;

    constructor(
        @inject('CurrentUserModel') currentUser: CurrentUserModel,
        @inject('Logger') baseLogger: Logger,
        @inject('DatadogService') @optional() private readonly datadogService?: DatadogService,
        @inject('AnalyticsService') @optional() private readonly analyticsService?: AnalyticsService,
    ) {
        this.#logger = baseLogger.getSubLogger({ name: 'feat:electron-renderer:syncToExternalsService' });

        this.#disposableStack.defer(
            effect(() => {
                this.#syncUserDetails(currentUser.user.value);
            }),
        );
    }

    #syncUserDetails(userDetails: UserDetails | undefined): void {
        if (TRACE) {
            this.#logger.trace('Setting user details', { userDetails });
        }

        try {
            void localforage.setItem(PERSISTED_CURRENT_USER, userDetails);
            this.datadogService?.setUserDetails(userDetails);

            // Sync user to analytics service
            if (userDetails) {
                this.analyticsService?.identify(userDetails);
            } else {
                this.analyticsService?.clearUser();
            }
        } catch (e) {
            this.#logger.error('Failed to sync user details', { err: e });
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
